// frontend/src/screens/ZodiacRouletteRoom.jsx

import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EmojiPicker from 'emoji-picker-react';
import { SIGN_EMOJI, genderEmoji, toAbsolutePhoto } from '../utils/rouletteUi';
import { REST_API_ORIGIN } from '../platform/endpoints';
import {
  socket,
  onMessage,
  onLikesUpdate,
  onRoomUsers,
  joinRoulette,
  onRoulettePartner,
  onWaitingPartner,
  onRouletteMatchEnded,
  getRoomUsers,
  sendRouletteMessage,
  toggleLike,
  cancelRoulette,
} from '../services/socket';

const API_ORIGIN = REST_API_ORIGIN;




function normalizeUsersPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.users)) return payload.users;
  return [];
}

function fileNameFromUrl(u = '') {
  try {
    const s = String(u);
    const noQuery = s.split('?')[0].split('#')[0];
    const parts = noQuery.split('/');
    return parts[parts.length - 1] || '';
  } catch { return ''; }
}

function normalizeStr(x = '') {
  return String(x).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}

// Intenta inferir el id del partner usando la lista de usuarios conectados a la sala
function inferPartnerId(partner, usersList) {
  if (!partner || !Array.isArray(usersList)) return null;
  const pName = normalizeStr(partner.fullName || partner.name || '');
  const pSign = normalizeStr(partner.zodiacSign || '');
  const pFile = fileNameFromUrl(partner.photoPath || '');

  // 1) match exacto por nombre + signo
  let found = usersList.find(u => normalizeStr(u.fullName || u.name || '') === pName && normalizeStr(u.zodiacSign || '') === pSign);
  if (found) return found._id || found.id || found.userId || null;

  // 2) match por nombre + filename de foto
  if (pFile) {
    found = usersList.find(u => {
      const f = fileNameFromUrl(u.photoPath || u.photo);
      return normalizeStr(u.fullName || u.name || '') === pName && f && f === pFile;
    });
    if (found) return found._id || found.id || found.userId || null;
  }

  // 3) último intento: solo por nombre, si es único
  const sameName = usersList.filter(u => normalizeStr(u.fullName || u.name || '') === pName);
  if (sameName.length === 1) {
    const u = sameName[0];
    return u._id || u.id || u.userId || null;
  }
  return null;
}

export default function ZodiacRouletteRoom() {
  const { t, i18n } = useTranslation(['home', 'common', 'rooms']);
  const [partner, setPartner] = useState(null);
  const [room, setRoom] = useState(null);
  const [waiting, setWaiting] = useState(true);
  const [usersRaw, setUsersRaw] = useState([]);
  const [messages, setMessages] = useState([]);
  const [likesState, setLikesState] = useState({});
  const [message, setMessage] = useState('');
  const [messageError, setMessageError] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const emojiPickerRef = useRef(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatBoxRef = useRef(null);
  const atBottomRef = useRef(true);


  const scrollToBottomImmediate = () => {
    const el = chatBoxRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };
  const navigate = useNavigate();

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const storedUserRef = useRef(storedUser);
  const seekingGenderRef = useRef(null);
  const partnerRef = useRef(null);
  const roomRef = useRef(null);
  const didInitRouletteRef = useRef(false);

  // === Preferencia de género buscado (persistida)
  const defaultSeeking = (() => {
    try {
      const saved = localStorage.getItem('seekingGender');
      if (saved) return saved;
    } catch {}
    const g = (storedUser.gender || '').toLowerCase();
    if (g === 'mujer') return 'hombre';
    if (g === 'hombre') return 'mujer';
    return 'todos';
  })();
  const [seekingGender, setSeekingGender] = useState(defaultSeeking);

  useEffect(() => {
    try { localStorage.setItem('seekingGender', seekingGender); } catch {}
  }, [seekingGender]);

  useEffect(() => {
    storedUserRef.current = storedUser;
  }, [storedUser]);

  useEffect(() => {
    seekingGenderRef.current = seekingGender;
  }, [seekingGender]);
  useEffect(() => {
    partnerRef.current = partner;
  }, [partner]);

  useEffect(() => {
    roomRef.current = room;
  }, [room]);

  const resetRouletteUi = useCallback(() => {
    partnerRef.current = null;
    roomRef.current = null;
    setWaiting(true);
    setPartner(null);
    setMessages([]);
    setUsersRaw([]);
    setLikesState({});
    setRoom(null);
  }, []);

  const buildRouletteProfile = useCallback((desiredSeeking) => {
    const currentUser = storedUserRef.current || {};
    return {
      fullName: currentUser.fullName,
      zodiacSign: currentUser.zodiacSign,
      photoPath: currentUser.photoPath,
      gender: currentUser.gender || null,
      seekingGender: desiredSeeking,
    };
  }, []);

  const restartRoulette = useCallback((desiredSeeking) => {
    cancelRoulette();
    resetRouletteUi();
    joinRoulette(buildRouletteProfile(desiredSeeking));
  }, [buildRouletteProfile, resetRouletteUi]);

  const handleChatScroll = () => {
    const el = chatBoxRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    atBottomRef.current = distanceFromBottom < 8;
  };

  useEffect(() => {
    const unsubscribeMatchEnded = onRouletteMatchEnded((data) => {
      const endedRoom = String(data?.room || data?.matchId || '');
      if (!roomRef.current || endedRoom !== String(roomRef.current)) return;
      resetRouletteUi();
    });

    onRoulettePartner((data) => {
     data.photoPath = toAbsolutePhoto(data.photoPath, API_ORIGIN);

      const desiredSeeking = seekingGenderRef.current || 'todos';
      const partnerGenderKey = String(data.gender || data.snapshotUser?.gender || '').toLowerCase();
      if (desiredSeeking !== 'todos' && partnerGenderKey && partnerGenderKey !== desiredSeeking) {
        restartRoulette(desiredSeeking);
        return;
      }

      const inferredId =
        data._id ||
        data.id ||
        data.userId ||
        (data.user && (data.user._id || data.user.id || data.user.userId));
      if (inferredId) {
        data._id = String(inferredId);
      }
      partnerRef.current = data;
      roomRef.current = data.room;
      setPartner(data);
      setRoom(data.room);
      setWaiting(false);
      setMessages([]);
      setUsersRaw([]);
      setLikesState({});
      getRoomUsers(data.room);
    });

    onWaitingPartner(() => {
      // Evita que un evento waiting viejo del lobby/reset pise un match recién armado.
      if (partnerRef.current || roomRef.current) return;
      resetRouletteUi();
    });

    onMessage((msg) => {
      const displayName = msg?.snapshotUser?.fullName || msg?.fullName || msg?.user || '';
      const photo = toAbsolutePhoto(msg?.snapshotUser?.photo || msg?.photo || '', API_ORIGIN);
      setMessages(prev => [
        ...prev,
        {
          id: msg.id || `m_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
          user: displayName,
          _mine: !!(
            msg?._mine ||
            msg?.isMine ||
            msg?.mine ||
            msg?.self ||
            msg?.fromSelf ||
            msg?.snapshotUser?._mine ||
            msg?.snapshotUser?.isMine
          ),
          senderUserId: String(
            msg?.userId ||
            msg?.senderId ||
            msg?.fromUserId ||
            msg?.snapshotUser?._id ||
            msg?.snapshotUser?.id ||
            msg?.snapshotUser?.userId ||
            ''
          ),
          text: msg?.text ?? msg?.message ?? '',
          snapshotUser: {
            fullName: displayName,
            photo,
            zodiacSign: msg?.snapshotUser?.zodiacSign || '',
            gender: msg?.snapshotUser?.gender ?? null,
            _id: msg?.snapshotUser?._id || msg?.snapshotUser?.id || msg?.snapshotUser?.userId || null,
          },
          timestamp: msg?.timestamp || Date.now(),
          system: !!msg?.system,
        }
      ]);
      requestAnimationFrame(() => {
        if (atBottomRef.current) {
          scrollToBottomImmediate();
        }
      });
    });

    onRoomUsers((payload) => setUsersRaw(normalizeUsersPayload(payload)));

    onLikesUpdate(({ messageId, likesCount }) => {
      setLikesState(prev => ({ ...prev, [messageId]: likesCount }));
    });

    const handleKeydown = (e) => {
      if (e.key === 'Escape') setShowEmojiPicker(false);
    };
    document.addEventListener('keydown', handleKeydown);

    const onPageHide = () => cancelRoulette();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') cancelRoulette();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);

    // A reconnect receives a new socket.id, so it must re-enter Roulette
    // through the authenticated singleton before it can recover a match.
    const onSocketConnect = () => {
      joinRoulette(buildRouletteProfile(seekingGenderRef.current || seekingGender || 'todos'));
    };
    socket.on('connect', onSocketConnect);

    joinRoulette(buildRouletteProfile(seekingGenderRef.current || seekingGender || 'todos'));

    return () => {
      unsubscribeMatchEnded();
      cancelRoulette();
      document.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      socket.off('connect', onSocketConnect);
    };
  }, [buildRouletteProfile, resetRouletteUi, restartRoulette, seekingGender]);

  useEffect(() => {
    if (!didInitRouletteRef.current) {
      didInitRouletteRef.current = true;
      return;
    }
    restartRoulette(seekingGender);
  }, [seekingGender, restartRoulette]);


  const partnerWithExtras = useMemo(() => {
    if (!partner) return null;
    const cleanSign = (partner.zodiacSign || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
   return { ...partner, emoji: SIGN_EMOJI[cleanSign] || '', photoPath: toAbsolutePhoto(partner.photoPath || '', API_ORIGIN) };
  }, [partner]);

  useEffect(() => {
    if (!partner || partner._id) return;
    const idGuess = inferPartnerId(partner, normalizeUsersPayload(usersRaw));
    if (idGuess) {
      setPartner(prev => prev ? { ...prev, _id: String(idGuess) } : prev);
      try { console.debug('[roulette] partnerId inferido desde usersRaw =', idGuess); } catch {}
    }
  }, [partner, usersRaw]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = message.trim();
    if (!text || !room) return;

    const result = await sendRouletteMessage({ room, text });
    if (!result?.ok) {
      setMessageError(result?.error || 'message_rejected');
      return;
    }

    setMessageError('');
    setMessage('');
    requestAnimationFrame(() => {
      if (atBottomRef.current) {
        scrollToBottomImmediate();
      }
    });
  };

  const addEmoji = (emojiData) => {
    const pickedEmoji = emojiData?.emoji || emojiData?.native || '';
    if (!pickedEmoji) return;

    setMessage(prev => `${prev}${pickedEmoji}`);
    setShowEmojiPicker(false);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const likeMessage = (msgId) => {
    if (!msgId || !room) return;
    const userKey = storedUser._id || storedUser.userId || storedUser.fullName || 'anon';
    toggleLike({ room, messageId: msgId, userKey });
  };


  const handleGoToPartnerProfile = () => {
    if (!partner) return;
    const partnerId =
      partner._id ||
      partner.id ||
      partner.userId ||
      (partner.user && (partner.user._id || partner.user.id || partner.user.userId));
    if (!partnerId) {
      // fallback: mostrar una pista para depurar si llega a ocurrir
      console.warn('[roulette] partner profile unavailable');
      return;
    }
    const minimal = {
      _id: String(partnerId),
      fullName: partner.fullName,
      zodiacSign: partner.zodiacSign,
      gender: partner.gender,
      photoPath: partner.photoPath,
      bio: partner.bio || ''
    };
    navigate(`/profile/${String(partnerId)}`, { state: { user: minimal } });
  };

  const handleBack = () => { cancelRoulette(); navigate(-1); };
  const handleImgError = (e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/default-profile.png'; };

  return (
    <div id="rouletteRoot" style={{ ...styles.container, isolation: 'isolate', position: 'relative', overflowX: 'hidden' }}>
      <style>{`
        @keyframes wheelSpinCenter {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to   { transform: translate(-50%, -50%) rotate(360deg); }
        }

        #rouletteRoot::before{
          content:"";
          position:fixed;
          inset:0;
          z-index:-3;
          pointer-events:none;
          background-image:
            radial-gradient(circle at 10% 8%, rgba(255,255,255,.85) 0 1px, transparent 1.8px),
            radial-gradient(circle at 78% 7%, rgba(255,215,0,.75) 0 1px, transparent 1.8px),
            radial-gradient(circle at 94% 20%, rgba(183,44,255,.76) 0 1px, transparent 1.8px),
            radial-gradient(circle at 24% 48%, rgba(255,255,255,.45) 0 1px, transparent 1.8px),
            radial-gradient(circle at 68% 56%, rgba(255,215,0,.50) 0 1px, transparent 1.8px),
            radial-gradient(circle at 44% 84%, rgba(255,255,255,.42) 0 1px, transparent 1.8px);
          opacity:.58;
        }

        @media (max-width: 540px) {
          #rouletteRoot .roulette-mode-filter {
            width:100% !important;
            justify-content:center !important;
          }
        }
      `}</style>

      {/* Base uniform violet background (prevents bottom tone line) */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: -3,
          background: 'radial-gradient(circle at 50% -8%, rgba(218,165,32,.16), transparent 22%), radial-gradient(circle at 12% 18%, rgba(183,44,255,.25), transparent 34%), radial-gradient(circle at 84% 64%, rgba(138,43,226,.20), transparent 34%), linear-gradient(180deg, #03000b 0%, #100020 48%, #05000d 100%)',
          pointerEvents: 'none'
        }}
      />


      {/* Centered spinning wheel (clean artwork, no black corners) */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          width: 'min(112svmin, 1180px)',
          height: 'min(112svmin, 1180px)',
          transform: 'translate(-50%, -50%)',
          backgroundImage: 'url("/logo/Zodiacus.jpeg")',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'drop-shadow(0 0 44px rgba(218,165,32,.22)) saturate(1.12)',
          borderRadius: '50%',
          animation: 'wheelSpinCenter 160s linear infinite',
          transformOrigin: 'center center',
          zIndex: -1,
          opacity: .18,
          pointerEvents: 'none',
        }}
      />

      <section style={styles.topBar}>
        <button type="button" onClick={handleBack} style={styles.topCircleButton} aria-label={t('common:actions.back')}>
          ←
        </button>
        <div style={styles.topTitleWrap}>
          <div style={styles.kicker}>{t('roulette.kicker')}</div>
          <h1 style={styles.title}>{t('roulette.modes.zodiacal.title')}</h1>
          <p style={styles.topSubtitle}><span style={styles.onlineDot}>●</span> {t('roulette.screen.liveSubtitle')}</p>
        </div>
      </section>

      {/* Preferencia de género */}
      <div className="roulette-mode-filter" style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        background: 'linear-gradient(145deg, rgba(7,0,19,.72), rgba(31,5,68,.50))',
        border: '1px solid rgba(218,165,32,.34)',
        padding: '10px 10px', borderRadius: 18, width: 'fit-content',
        boxShadow: '0 18px 40px rgba(0,0,0,.30), inset 0 1px 0 rgba(255,255,255,.08)',
        margin: '0 auto 12px',
        backdropFilter: 'blur(16px) saturate(1.12)',
        WebkitBackdropFilter: 'blur(16px) saturate(1.12)'
      }}>
        <span style={{ fontWeight: 950, color: '#F3C96B', letterSpacing: '.04em' }}>{t('roulette.room.gender.label')}</span>
        {[
          { key: 'hombre' },
          { key: 'mujer' },
          { key: 'todos' },
        ].map(opt => (
          <button
            key={opt.key}
            type="button"
            aria-pressed={seekingGender === opt.key}
            onClick={() => {
              setSeekingGender(opt.key);
            }}
            style={{
              border: '1px solid rgba(218,165,32,0.45)',
              background: seekingGender === opt.key
                ? 'linear-gradient(135deg,#8A2BE2,#6F1CCB,#DAA520)'
                : 'linear-gradient(135deg, rgba(255,255,255,.10), rgba(255,255,255,.045))',
              color: seekingGender === opt.key ? '#fff' : '#FFE8A3',
              fontWeight: 800,
              borderRadius: 10,
              padding: '8px 10px',
              cursor: 'pointer',
              fontSize: 14,
              boxShadow: seekingGender === opt.key ? '0 2px 10px rgba(0,0,0,0.25)' : 'none'
            }}
            title={t(`roulette.room.gender.options.${opt.key}.title`)}
          >
            {t(`roulette.room.gender.options.${opt.key}.label`)}
          </button>
        ))}
      </div>

      {waiting ? (
        <div style={styles.infoBox}>
          <h3 style={styles.infoTitle}>{t('roulette.room.waiting.title')}</h3>
          <p style={styles.infoText}>{t('roulette.room.waiting.description')}</p>
        </div>
      ) : (
        <>
          {partnerWithExtras && (
            <>
              <div style={styles.headerCard}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, flexWrap:'nowrap', width:'100%' }}>
                  <img
                    src={partnerWithExtras.photoPath}
                    alt={t('rooms:chat.profile.avatarAlt', { name: partnerWithExtras.fullName || t('roulette.room.anonymous') })}
                    style={styles.headerAvatar}
                    onError={handleImgError}
                    onClick={handleGoToPartnerProfile}
                    role="button"
                    tabIndex={0}
                    title={t('rooms:chat.profile.view')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGoToPartnerProfile(); } }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', flexWrap: 'nowrap' }}>
                      <span style={styles.headerName}>{partnerWithExtras.fullName}</span>
                      {partnerWithExtras.emoji ? (
                        <span style={styles.headerSign} title={partnerWithExtras.zodiacSign}>
                          {partnerWithExtras.emoji}
                        </span>
                      ) : null}
                      {genderEmoji(partnerWithExtras.gender) ? (
                        <span style={styles.headerGender}>{genderEmoji(partnerWithExtras.gender)}</span>
                      ) : null}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 2, justifyContent:'center', flexWrap:'nowrap' }}>
                      <button
                        type="button"
                        onClick={handleGoToPartnerProfile}
                        style={styles.addBtnInline}
                        title={t('rooms:chat.profile.view')}
                        aria-label={t('rooms:chat.profile.view')}
                      >
                        {t('rooms:chat.profile.view')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          restartRoulette(seekingGenderRef.current || seekingGender || 'todos');
                        }}
                        style={styles.nextButton}
                      >
                        🔄 {t('roulette.room.actions.next')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}


          {/* CHAT */}
          <div style={styles.chatBox} ref={chatBoxRef} onScroll={handleChatScroll} id="chatBox">
            {messages.map((msg, i) => {
              if (msg.system) {
                return (
                  <div key={msg.id || `sys-${i}`} style={styles.systemMsg}>
                    {msg.text}
                  </div>
                );
              }
              const likesCount = likesState[msg.id] ?? msg.likesCount ?? 0;
              const displayName = msg?.snapshotUser?.fullName || msg?.user || t('roulette.room.anonymous');
              const photo = toAbsolutePhoto(msg?.snapshotUser?.photo || '', API_ORIGIN);
              const gEmoji = genderEmoji(msg?.snapshotUser?.gender);
              const date = new Date(msg.timestamp);
              const hh = String(date.getHours()).padStart(2, '0');
              const mm = String(date.getMinutes()).padStart(2, '0');
              const signKey = (msg?.snapshotUser?.zodiacSign || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
              const zEmoji = SIGN_EMOJI[signKey] || '';

              const currentUserId = String(storedUser._id || storedUser.id || storedUser.userId || '');
              const msgSnapshotId = String(msg?.snapshotUser?._id || msg?.senderUserId || '');
              const isMine =
                !!msg?._mine ||
                (!!currentUserId && !!msgSnapshotId && currentUserId === msgSnapshotId);
              const containerStyle = isMine ? { ...styles.messageItem, ...styles.messageItemMine } : styles.messageItem;
              const bubbleStyle = isMine ? { ...styles.messageBubble, ...styles.messageBubbleMine } : styles.messageBubble;
              const footerStyle = isMine ? { ...styles.messageFooter, justifyContent: 'flex-end' } : styles.messageFooter;
              const nameRowStyle = isMine ? { ...styles.messageUserName, textAlign: 'right' } : styles.messageUserName;

              return (
                <div key={msg.id || i} style={containerStyle}>
                  <img src={photo} alt={t('rooms:chat.profile.avatarAlt', { name: displayName })} style={styles.messagePhoto} onError={handleImgError}/>
                  <div style={{ flex: 1 }}>
                    <div style={nameRowStyle}>
                      <span style={isMine ? styles.nameTagMine : styles.nameTagOther}>
                        <span style={{ ...styles.messageName, ...(isMine ? styles.nameMine : styles.nameOther) }}>
                          {displayName}
                        </span>
                      </span>
                      {zEmoji ? (
                        <span style={styles.signBadge} title={msg?.snapshotUser?.zodiacSign}>
                          {zEmoji}
                        </span>
                      ) : null}
                      {gEmoji ? <span style={styles.genderBadge}>{gEmoji}</span> : null}
                    </div>
                    <div style={bubbleStyle}>{msg.text}</div>
                    <div style={footerStyle}>
                      <span style={styles.messageTime}>{hh}:{mm}</span>
                      <div style={styles.likeContainer}>
                        <button
                          style={styles.likeButton}
                          onClick={() => likeMessage(msg.id)}
                          aria-label={t('rooms:chat.likes.toggle')}
                          title={t('rooms:chat.likes.title')}
                        >
                          {likesCount > 0 ? '❤️' : '🤍'}
                        </button>
                        <span style={styles.likeCount}>{likesCount}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>

          {showEmojiPicker && (
            <div ref={emojiPickerRef} style={styles.emojiPickerContainer}>
              <EmojiPicker
                onEmojiClick={addEmoji}
                locale={(i18n.resolvedLanguage || i18n.language || 'es').toLowerCase().startsWith('en') ? 'en' : 'es'}
                lazyLoadEmojis={true}
                searchDisabled={true}
                skinTonesDisabled={false}
                emojiStyle="native"
                theme="light"
                autoFocusSearch={false}
                width="100%"
              />
            </div>
          )}
          {/* COMPOSER */}
          <div style={styles.composerWrap}>
            <button
              type="button"
              onClick={() => setShowEmojiPicker(prev => !prev)}
              style={styles.emojiToggleButton}
              aria-label={t('rooms:chat.emoji.toggle')}
              title={t('rooms:chat.emoji.title')}
            >
              <span style={styles.emojiButtonIcon}>😀</span>
            </button>
            <form onSubmit={handleSubmit} style={styles.form}>
              <input
                ref={inputRef}
                id="rouletteMessage"
                name="rouletteMessage"
                type="text"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (messageError) setMessageError('');
                }}
                placeholder={t('rooms:chat.composer.placeholder')}
                style={styles.input}
                autoComplete="off"
              />
              <button type="submit" style={styles.sendButton} aria-label={t('rooms:chat.composer.sendMessage')}>➤</button>
            </form>
          </div>
          {messageError && <p role="alert" style={styles.messageError}>{t('roulette.room.errors.sendFailed', { error: messageError })}</p>}
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)',
    paddingBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))',
    backgroundColor: 'transparent',
    color: '#fff',
    minHeight: '100dvh',
    position: 'relative',
    fontFamily: "'Poppins', sans-serif",
    overscrollBehaviorY: 'none',
    boxSizing: 'border-box',
  },
  topBar: {
    display: 'grid',
    gridTemplateColumns: '40px minmax(0, 1fr)',
    alignItems: 'center',
    gap: 9,
    marginBottom: 12,
    padding: '10px 10px',
    borderRadius: 24,
    border: '1px solid rgba(218,165,32,.32)',
    background: 'linear-gradient(145deg, rgba(7,0,19,.72), rgba(31,5,68,.50))',
    boxShadow: '0 18px 42px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.08)',
    backdropFilter: 'blur(16px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
  },
  topCircleButton: {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: '50%',
    border: '1px solid rgba(218,165,32,.44)',
    background: 'radial-gradient(circle at 34% 22%, rgba(255,255,255,.14), transparent 34%), rgba(10,0,26,.66)',
    color: '#FFE8A3',
    fontSize: 21,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    lineHeight: 1,
    padding: 0,
    boxShadow: '0 12px 28px rgba(0,0,0,.32)',
    boxSizing: 'border-box',
  },
  topTitleWrap: {
    minWidth: 0,
  },
  kicker: {
    color: '#F3C96B',
    fontSize: '.58rem',
    fontWeight: 950,
    letterSpacing: '.18em',
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  topSubtitle: {
    margin: '8px 0 0',
    color: 'rgba(255,255,255,.78)',
    fontSize: 13,
    fontWeight: 850,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  onlineDot: {
    color: '#27e38a',
    textShadow: '0 0 12px rgba(39,227,138,.85)',
    margin: '0 4px',
  },
  title: {
    margin: 0,
    color: '#F3C96B',
    fontFamily: 'Georgia, serif',
    fontSize: 'clamp(1.65rem, 7.2vw, 2.35rem)',
    lineHeight: .96,
    letterSpacing: '.01em',
    textShadow: '0 0 24px rgba(218,165,32,.34)',
    whiteSpace: 'nowrap',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subTitleBar: { display: 'flex', justifyContent: 'center', margin: '20px 0 18px' },
  subTitleText: {
    padding: '3px 8px',
    borderRadius: 999,
    background: 'rgba(0,0,0,0.28)',
    border: '1px solid rgba(218,165,32,0.25)',
    color: '#FFF6D6',
    fontSize: 'clamp(0.68rem, 1.6vw, 0.78rem)',
    fontWeight: 600,
    letterSpacing: 0.15,
    textTransform: 'uppercase',
    textShadow: '0 1px 2px rgba(0,0,0,0.35)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    userSelect: 'none',
    fontFamily: "'Inter', 'Poppins', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  },

  // Header (match)
  headerCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'nowrap',
    background: 'linear-gradient(145deg, rgba(7,0,19,.78), rgba(31,5,68,.58))',
    border: '1px solid rgba(218,165,32,.38)',
    borderRadius: 22,
    padding: '10px 12px',
    marginBottom: 12,
    boxShadow: '0 18px 40px rgba(0,0,0,.32), inset 0 1px 0 rgba(255,255,255,.08)',
    backdropFilter: 'blur(16px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flex: '1 1 auto',
    minWidth: 0,
    maxWidth: 'calc(100% - 148px)'
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: '0 0 auto',             // el botón mantiene su tamaño
    marginLeft: 'auto',
    flexShrink: 0,
    width: 'auto'
  },
  headerAvatar: {
    width: 'clamp(80px, 22vw, 130px)',
    height: 'clamp(80px, 22vw, 130px)',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '4px solid #DAA520',
    boxShadow: '0 6px 18px rgba(0,0,0,0.45), 0 0 0 3px rgba(75,0,130,0.28) inset'
  },
  headerNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#fff',
    flexWrap: 'nowrap',           // ⬅️ fuerza una sola línea
    minWidth: 0,
    flex: '1 1 auto',
    whiteSpace: 'nowrap',         // ⬅️ evita salto de línea
    overflowX: 'visible',
    WebkitOverflowScrolling: 'touch',
  },
  headerName: {
    display: 'inline-block',
    flex: '0 0 auto',             // ⬅️ no se contrae el nombre
    minWidth: 'auto',
    padding: '4px 10px',
    borderRadius: 999,
    fontFamily: '"Cinzel", "Cormorant Garamond", serif',
    fontWeight: 900,
    letterSpacing: '0.35px',
    fontSize: 'clamp(1.05rem, 4.2vw, 1.2rem)',
    lineHeight: 1.1,
    color: '#FFE49A',
    textShadow: '0 1px 2px rgba(0,0,0,0.55), 0 0 10px rgba(255,228,154,0.35)',
    background: 'linear-gradient(180deg, rgba(0,0,0,0.55), rgba(25,0,50,0.45))',
    border: '1px solid rgba(218,165,32,0.6)',
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.06)',
    whiteSpace: 'nowrap',         // ⬅️ nombre en una sola línea
  },
  headerSign: {
    fontSize: '1rem',
    flex: '0 0 auto',             // ⬅️ no se contrae ni crece, siempre visible
    marginLeft: 6,
    display: 'inline-block',
    lineHeight: 1,
    verticalAlign: 'middle',
  },
  headerGender: {
    fontSize: '1rem',
    flex: '0 0 auto',             // ⬅️ no se contrae ni crece, siempre visible
    marginLeft: 6,
    display: 'inline-block',
    lineHeight: 1,
    verticalAlign: 'middle',
  },
  headerHint: { fontSize:'0.85rem', opacity:0.95, color:'#FFFFFF', textShadow:'0 1px 2px rgba(0,0,0,0.55), 0 0 6px rgba(0,0,0,0.25)' },
  headerSignText: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#FFD700',
    textShadow: '0 1px 3px rgba(0,0,0,0.6), 0 0 4px #4B0082',
    marginTop: 2,
  },
  nextButton: {
    padding: '8px 12px',
    background: 'linear-gradient(135deg,#8A2BE2 0%, #6F1CCB 48%, #DAA520 100%)',
    color: '#fff',
    border: '1px solid rgba(255,215,0,.45)',
    borderRadius: 999,
    fontWeight: 950,
    cursor: 'pointer',
    boxShadow: '0 10px 22px rgba(0,0,0,.28), 0 0 14px rgba(218,165,32,.16)',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    alignSelf: 'center',
    flexShrink: 0,
  },
  addBtnInline: {
    marginRight: 10,
    padding: '8px 12px',
    background: 'rgba(255,255,255,.10)',
    color: '#FFE8A3',
    border: '1px solid rgba(218,165,32,.42)',
    borderRadius: 999,
    fontWeight: 900,
    fontSize: '0.88rem',
    cursor: 'pointer',
    boxShadow: '0 8px 18px rgba(0,0,0,.22)',
    lineHeight: 1,
    minHeight: 34,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    whiteSpace: 'nowrap',
  },

  usersBox: {
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    padding: '6px 8px',
    borderRadius: 8,
    color: '#fff',
    maxWidth: 320,
    marginBottom: 12,
    border: '1px solid rgba(218,165,32,0.35)',
  },
  usersTitle: { fontWeight: 'bold', color: '#DAA520', marginBottom: 6, fontSize: '0.9rem' },
  usersList: {
    listStyle: 'none',
    paddingLeft: 0,
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 6
  },
  userChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(218,165,32,0.35)',
    padding: '6px 10px',
    borderRadius: 12,
    flexWrap: 'nowrap',
    minWidth: 0
  },
  userChipAvatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    border: '1px solid #DAA520',
    objectFit: 'cover',
    flexShrink: 0,
    boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
  },
  userChipName: {
    fontFamily: '"Cinzel", "Cormorant Garamond", serif',
    fontWeight: 800,
    letterSpacing: '0.3px',
    fontSize: 'clamp(0.8rem, 2.6vw, 0.9rem)',
    lineHeight: 1.15,
    color: '#FFE49A',
    textShadow: '0 1px 2px rgba(0,0,0,0.45), 0 0 6px rgba(255,228,154,0.35)',
    flex: '0 1 auto',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  userChipSign: { fontSize:'1.2rem', flexShrink: 0, marginLeft: 6 },
  userChipGender: { fontSize:'1.2rem', flexShrink: 0, marginLeft: 4 },

  systemMsg: {
    textAlign: 'center',
    fontStyle: 'italic',
    color: '#FFE8A3',
    marginBottom: 10,
    background: 'rgba(255,255,255,.08)',
    border: '1px solid rgba(218,165,32,.22)',
    borderRadius: 14,
    padding: '7px 12px',
    maxWidth: '78%',
    alignSelf: 'center',
    fontWeight: 700,
    boxShadow: '0 8px 18px rgba(0,0,0,.18)',
  },
  // Ajustes de densidad y legibilidad
  messageItem: {
    borderRadius: 18,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 16,
    padding: 10,
    maxWidth: '75%',
    alignSelf: 'flex-start',
    boxShadow: '0 12px 24px rgba(0,0,0,.22)',
    wordBreak: 'break-word',
    background: 'linear-gradient(135deg, rgba(255,255,255,.10), rgba(255,255,255,.045))',
    border: '1px solid rgba(218,165,32,.28)',
  },
  messageItemMine: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
    background: 'linear-gradient(135deg, rgba(138,43,226,.22), rgba(75,0,130,.15))',
    border: '1px solid rgba(218,165,32,.26)',
  },

messagePhoto: {
  width: 'clamp(32px, 7vw, 36px)',   // antes 36px
  height: 'clamp(32px, 7vw, 36px)',
  borderRadius: '50%',
  objectFit: 'cover',
  border: '2px solid #DAA520',
  flexShrink: 0
},

  messageUserName: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    fontWeight: 600,
    color: '#4B0082',
    marginBottom: 2,
    lineHeight: 1.2,
    minWidth: 0,
  },
  messageName: {
    marginRight: 0,
    display: 'inline-block',
    flex: '0 0 auto',
    maxWidth: 'none',
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'nowrap',
    fontFamily: '"Cinzel", "Cormorant Garamond", serif',
    fontWeight: 900,
    letterSpacing: '0.3px',
    fontSize: 'clamp(0.88rem, 3.2vw, 1.02rem)',
    lineHeight: 1.15,
    textTransform: 'none',
  },
  nameMine: {
    color: '#2a0051',
    textShadow: '0 1px 1px rgba(255,255,255,0.25), 0 0 1px rgba(0,0,0,0.65)'
  },
  nameOther: {
    color: '#FFF6D6',
    textShadow: '0 1px 1px rgba(0,0,0,0.6)'
  },
  nameTagMine: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 10,
    background: 'linear-gradient(135deg, rgba(255,226,138,0.9), rgba(218,165,32,0.9))',
    border: '1px solid rgba(218,165,32,0.65)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.06)',
    marginRight: 6,
    flex: '0 0 auto',
    maxWidth: 'none',
  },
  nameTagOther: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 10,
    background: 'linear-gradient(135deg, rgba(22,10,40,0.78), rgba(35,0,70,0.72))',
    border: '1px solid rgba(75,0,130,0.45)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.04)',
    marginRight: 6,
    flex: '0 0 auto',
    maxWidth: 'none',
  },

signBadge: {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  fontSize: '0.8rem',
  lineHeight: 1,
  width: 18,
  height: 18,
  marginLeft: 0,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #fff6c2 0%, #ffe89a 100%)',
  border: '1px solid rgba(218,165,32,0.6)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
  color: '#4B0082',
  fontWeight: 700,
  userSelect: 'none',
},

  // Un touch menos de “marco” en el chat para ganar espacio útil
  chatBox: {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(218,165,32,.38)',
    padding: 12,
    minHeight: 360,
    height: 'calc(100dvh - 250px)',
    overflowY: 'auto',
    marginBottom: 80,
    position: 'relative',
    background: 'linear-gradient(145deg, rgba(7,0,19,.82), rgba(31,5,68,.62))',
    backdropFilter: 'blur(18px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(18px) saturate(1.12)',
    color: '#fff',
    borderRadius: 24,
    boxShadow: '0 24px 54px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.08)',
    scrollBehavior: 'auto',
    WebkitOverflowScrolling: 'touch',
    overflowAnchor: 'none',
    scrollbarGutter: 'stable',
    overscrollBehavior: 'contain',
  },
  genderBadge: {
    marginLeft: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    fontSize: '0.95rem',
    lineHeight: 1,
    userSelect: 'none',
    filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
  },
  messageFooter: { marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  messageTime: { fontSize: '0.75rem', color: 'rgba(255,255,255,.62)', fontStyle: 'italic', marginRight: 'auto' },
  likeContainer: { display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' },
  likeButton: { fontSize: '1.3rem', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, userSelect: 'none' },
  likeCount: { fontSize: '0.9rem', color: '#FFE8A3', fontWeight: '700', userSelect: 'none' },

  emojiToggleButton: {
    width: 42,
    height: 42,
    minWidth: 42,
    minHeight: 42,
    flex: '0 0 42px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    margin: 0,
    lineHeight: 1,
    fontSize: 0,
    background: 'rgba(255,255,255,.10)',
    border: '1px solid rgba(218,165,32,.40)',
    borderRadius: '50%',
    cursor: 'pointer',
    color: '#FFE8A3',
    boxShadow: '0 10px 20px rgba(0,0,0,.26)',
    overflow: 'hidden',
    WebkitTapHighlightColor: 'transparent',
    appearance: 'none',
    WebkitAppearance: 'none',
    pointerEvents: 'auto',
  },
  emojiButtonIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    lineHeight: 1,
    fontSize: 22,
    transform: 'translateY(-1px)',
    pointerEvents: 'none',
  },

  // Burbujas
  messageBubble: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #ffe28a 0%, #DAA520 100%)',
    color: '#4B0082',
    padding: '10px 14px',
    borderRadius: 16,
    border: '1px solid rgba(218,165,32,0.45)',
    boxShadow: '0 6px 14px rgba(0,0,0,0.12), 0 0 0 1px rgba(218,165,32,0.12) inset',
    maxWidth: '65vw',
    marginTop: 4,
  },
  messageBubbleMine: {
    background: 'linear-gradient(135deg, #7c3aed 0%, #4B0082 100%)',
    color: '#FFD665',
    textShadow: '0 1px 2px rgba(0,0,0,0.45), 0 0 8px rgba(255,214,101,0.35)',
    border: '1px solid rgba(91,33,182,0.5)',
  },

  // Composer flotante
  composerWrap: {
    position: 'fixed',
    left: 12,
    right: 12,
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 10px)',
    padding: '9px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    zIndex: 900,
    pointerEvents: 'none',
    borderRadius: 22,
    background: 'linear-gradient(145deg, rgba(7,0,19,.94), rgba(31,5,68,.88))',
    border: '1px solid rgba(218,165,32,.40)',
    boxShadow: '0 18px 42px rgba(0,0,0,.46), inset 0 1px 0 rgba(255,255,255,.08)',
    backdropFilter: 'blur(18px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(18px) saturate(1.12)',
  },
  emojiPickerContainer: {
    position: 'fixed',
    left: '12px',
    right: '12px',
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)',
    zIndex: 5000,
    maxWidth: 440,
    margin: '0 auto',
    maxHeight: 'min(46dvh, 360px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.96)',
    border: '1px solid rgba(75,0,130,0.35)',
    borderRadius: 18,
    backdropFilter: 'blur(10px) saturate(1.04)',
    WebkitBackdropFilter: 'blur(10px) saturate(1.04)',
    padding: 10,
    boxShadow: '0 18px 42px rgba(0, 0, 0, 0.36)',
    pointerEvents: 'auto',
    touchAction: 'manipulation',
    boxSizing: 'border-box',
  },
  form: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    background: 'rgba(255,255,255,.10)',
    border: '1px solid rgba(218,165,32,.46)',
    borderRadius: 999,
    padding: 6,
    boxShadow: 'inset 0 2px 10px rgba(0,0,0,.22)',
    pointerEvents: 'auto',
  },
  input: {
    flex: 1,
    minWidth: 0,
    padding: '11px 12px',
    fontSize: '0.98rem',
    borderRadius: 999,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: '#fff',
    fontWeight: 750,
  },
  sendButton: {
    width: 42,
    height: 42,
    minWidth: 42,
    padding: 0,
    background: 'linear-gradient(135deg,#8A2BE2 0%, #6F1CCB 48%, #DAA520 100%)',
    color: '#fff',
    border: '1px solid rgba(255,215,0,.45)',
    borderRadius: 999,
    fontWeight: 950,
    cursor: 'pointer',
    boxShadow: '0 12px 24px rgba(0,0,0,.32), 0 0 16px rgba(218,165,32,.20)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    fontSize: 18,
  },
  messageError: {
    margin: '7px 8px 0',
    color: '#ffd1d1',
    fontSize: 12,
    fontWeight: 700,
  },

  infoBox: {
    background: 'linear-gradient(145deg, rgba(7,0,19,.76), rgba(31,5,68,.54))',
    color: '#fff',
    padding: 20,
    borderRadius: 22,
    maxWidth: 520,
    margin: '40px auto 0',
    textAlign: 'center',
    border: '1px solid rgba(218,165,32,.38)',
    boxShadow: '0 24px 54px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.08)',
    backdropFilter: 'blur(16px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
  },
  infoTitle: { fontSize: '1.3rem', fontWeight: 'bold', color: '#DAA520', margin: 0 },
  infoText: { marginTop: 8, opacity: 0.9 },
};
